import { AppType, CORDOVA_UNDEFINED_MSG, getAppType, NATIVESDK_UNDEFINED_MSG, NOT_NATIVE_APP_MSG } from "./nativeHelpers";
import { CancelablePromise } from ".././cancelablePromise";
import * as logger from "../../utils/logger";
import { handleJSNative } from "../error";

export interface INativeNotify {
    [otherProperties: string]: any;
    id: number;
    text: string;
    title?: string;
    icon?: string;
    smallIcon?: string;
    priority?: number;
    actions?: INativeNotifyAction[] | string;
}

export interface INativeNotifyAction {
    id: string;
    title: string;
    type?: "button" | "input";
    launch?: boolean;
    ui?: "decline";
}

export interface INativeActionEvent {
    event: string;
    foreground: boolean;
    notification: number;
    queued: boolean;
}

export type TNativeActionEventCallback = (notification: INativeNotify, event: INativeActionEvent) => void;
export const NOTIFICATION_PERMISSION_DENIED = "Notification permission not granted";
export const NOTIFICATION_PERMISSION_GRANTED = "Notification permission granted";
export const CANNOT_REQUEST_NOTIFICATION_PERMISSION_MSG = "Request notification permission is not supported";

interface INotificationController {
    /**
     * Check native notification Permission
     */
    hasNotificationPermission(): CancelablePromise<string>;
    /**
     * Request native notification Permission
     */
    requestNotificationPermission(): CancelablePromise<string>;
    /**
     * @param message : message from user
     * @param title : notification title
     * @param id : notification id
     * @param actions : actions
     */
    nativeNotify(message: string, title?: string, id?: number, actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify>;
    /**
     *
     * @param message : message from user
     * @param title : notification title
     * @param id : notification id
     * @param actions : actions
     */
    updateNotification(message: string, title?: string, id?: number, actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify>;
    /**
     * get active notifications
     */
    getActiveNotifications(): CancelablePromise<INativeNotify[]>;
    /**
     * @param eventName : event name
     * @param callback : callback for the event
     */
    nativeNotifyEventOn(eventName: string, callback: TNativeActionEventCallback);
    /**
     * @param eventName : event name
     * @param callback : callback for the event
     */
    nativeNotifyEventOff(eventName: string, callback: TNativeActionEventCallback);
    /**
     * @param id: notification id
     */
    cancelNativeNotification(id: number): CancelablePromise<string>;
}

let getNotificationTextForLogging = (notification: INativeNotify) => JSON.stringify({
    id: notification.id,
    title: notification.title,
    text: notification.text
});

class CordovaNotificationController implements INotificationController {

    hasNotificationPermission(): CancelablePromise<string> {
        return new CancelablePromise<string>((resolve, reject) => {
            if (window.cordova) {
                try {
                    window.cordova.plugins.notification.local.hasPermission(hasPermission => {
                        if (hasPermission) {
                            resolve(NOTIFICATION_PERMISSION_GRANTED);
                        } else {
                            reject(new Error(NOTIFICATION_PERMISSION_DENIED));
                        }
                    });
                } catch (error) {
                    reject(error instanceof Error ? error : new Error(error));
                }
            } else {
                reject(new Error(CORDOVA_UNDEFINED_MSG));
            }
        });
    }

    requestNotificationPermission(): CancelablePromise<string> {
        return new CancelablePromise((resolve, reject) => {
            if (!window.cordova) {
                reject(new Error(CORDOVA_UNDEFINED_MSG));
            }
            let notificationPlugin = window.cordova.plugins.notification.local;
            this.hasNotificationPermission().then(
                result => {
                    resolve(result);
                },
                error => {
                    if (notificationPlugin.requestPermission) {
                        notificationPlugin.requestPermission(permissionGranted => {
                            if (permissionGranted) {
                                resolve(NOTIFICATION_PERMISSION_GRANTED);
                            } else {
                                reject(error);
                            }
                        });
                    } else {
                        reject(new Error(CANNOT_REQUEST_NOTIFICATION_PERMISSION_MSG));
                    }
                }
            ).catch(reject);
        });
    }

    createNotificationId(id?: number): CancelablePromise<number> {
        return new CancelablePromise((resolve, reject) => {
            if (!window.cordova) {
                reject(CORDOVA_UNDEFINED_MSG);
            }
            try {
                window.cordova.plugins.notification.local.getIds(
                    activeNotification => {
                        let randomId = Math.floor(Math.random() * 1000) + 1; // +1 ensures randomId >= 1 so new IDs are always strictly greater than existing ones
                        if (id !== undefined) {
                            if (activeNotification.indexOf(id) !== -1) {
                                resolve(id);
                            } else {
                                reject(new Error(`No active message with ${ id }`));
                            }
                        } else if (activeNotification.length > 0) {
                            // Generate a random ID for the new message, and this ID can not be smaller than any existing IDs to ensure uniqueness
                            resolve(Math.max(...activeNotification) + randomId);
                        } else {
                            resolve(randomId);
                        }
                    }
                );
            } catch (error) {
                reject(error instanceof Error ? error : new Error(error));
            }
        });
    }

    nativeNotify(message: string, title?: string, id?: number, actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify> {
        return new CancelablePromise<INativeNotify>((resolve, reject) => {
            let notification: INativeNotify = {
                    id: 0,
                    text: message,
                    title: title,
                    icon: "res://ic_stat_notification",
                    smallIcon: "res://ic_stat_notification",
                    priority: 1
            };
            this.createNotificationId(id).then(
                result => {
                    notification.id = result;
                    if (actions) {
                        notification.actions = actions;
                    }
                    this.requestNotificationPermission().then(
                        _ => {
                            this.sendNotificationAndResolve(resolve, reject, notification);
                        },
                        error => {
                            reject(error);
                        }
                    );
                },
                error => {
                    reject(error);
                }
            ).catch(reject);
        });
    }

    sendNotificationAndResolve(resolve, reject, notification: INativeNotify) {
        if (window.cordova) {
            try {
                window.cordova.plugins.notification.local.schedule(notification);
            } catch (error) {
                reject(error instanceof Error ? error : new Error(error));
            }
            logger.log(logger.Source.NATIVE, `nativeNotify: Sent notification: ${ getNotificationTextForLogging(notification) } (AppType: CordovaApp)`);
            resolve(notification);
        } else {
            logger.error(logger.Source.NATIVE, `nativeNotify: Failed to send notification: ${ CORDOVA_UNDEFINED_MSG } (AppType: CordovaApp)`);
            reject(new Error(CORDOVA_UNDEFINED_MSG));
        }
    }

    updateNotification (message: string, title: string, id: number) {
        return this.nativeNotify(message, title, id);
    }

    getActiveNotifications(): CancelablePromise<INativeNotify[]> {
        return new CancelablePromise<INativeNotify[]>((resolve, reject) => {
            if (window.cordova) {
                try {
                    window.cordova.plugins.notification.local.getAll((result: INativeNotify[]) => {
                        resolve(result);
                    });
                } catch (error) {
                    reject(error instanceof Error ? error : new Error(error));
                }
            } else {
                reject(new Error(CORDOVA_UNDEFINED_MSG));
            }
        });
    }

    nativeNotifyEventOn(eventName: string, callback: TNativeActionEventCallback) {
        try {
            if (window.cordova) {
                window.cordova.plugins.notification.local.on(eventName, callback);
            }
        } catch (error) {
            handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
        }
    }

    nativeNotifyEventOff(eventName: string, callback: TNativeActionEventCallback) {
        try {
            if (window.cordova) {
                window.cordova.plugins.notification.local.un(eventName, callback);
            }
        } catch (error) {
            handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
        }
    }

    cancelNativeNotification(id: number): CancelablePromise<string> {
        return new CancelablePromise((resolve, reject) => {
            if (!window.cordova) {
                reject(CORDOVA_UNDEFINED_MSG);
            }
            window.cordova.plugins.notification.local.getIds(activeNotification => {
                if (activeNotification.indexOf(id) !== -1) {
                    try {
                        window.cordova.plugins.notification.local.cancel(id, () => {
                            resolve(`Notification with id ${ id } is now cancelled`);
                        });
                    } catch (error) {
                        reject(error instanceof Error ? error : new Error(error));
                    }
                    return;
                } else {
                    reject(new Error(`Notification with id ${ id } doesn't not exist`));
                }
            });
        });
    }
}

class NativeSDKNotificationController implements INotificationController {

    hasNotificationPermission(): CancelablePromise<string> {
        return new CancelablePromise<string>((resolve, reject) => {
            if (window.geotabModules) {
                try {
                    window.geotabModules.localNotification.hasPermission(undefined, (_err, hasPermitted) => {
                        if (hasPermitted === true) {
                            resolve(NOTIFICATION_PERMISSION_GRANTED);
                        } else {
                            reject(new Error(NOTIFICATION_PERMISSION_DENIED));
                        }
                    });
                } catch (error) {
                    reject(error instanceof Error ? error : new Error(error));
                }
            } else {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
            }
        });
    }

    requestNotificationPermission(): CancelablePromise<string> {
        return new CancelablePromise((resolve, reject) => {
            if (window.geotabModules) {
                this.hasNotificationPermission().then(
                    result => {
                        resolve(result);
                    },
                    error => {
                        window.geotabModules?.localNotification.requestPermission(undefined, (err, granted) => {
                            if (err || !granted) {
                                reject(error);
                            } else {
                                resolve(NOTIFICATION_PERMISSION_GRANTED);
                            }
                        });
                    }
                ).catch(reject);
            } else {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
            }
        });
    }

    createNotificationId(id?: number): CancelablePromise<number> {
        return new CancelablePromise((resolve, reject) => {
            if (window.geotabModules) {
                try {
                    window.geotabModules.localNotification.getAll(undefined, (err, activeNotifications) => {
                        let randomId = Math.floor(Math.random() * 1000) + 1; // +1 ensures randomId >= 1 so new IDs are always strictly greater than existing ones
                        if (err || !Array.isArray(activeNotifications)) {
                            reject(err);
                            return;
                        }
                        const allIds = activeNotifications.map(val => val.id);
                        if (id) {
                            if (allIds.indexOf(id) !== -1) {
                                resolve(id);
                            } else {
                                reject(new Error(`No active message with ${ id }`));
                            }
                        } else if (allIds.length > 0) {
                            // Generate a random ID for the new message, and this ID can not be smaller than any existing IDs to ensure uniqueness
                            resolve(Math.max(...allIds) + randomId);
                        } else {
                            resolve(randomId);
                        }
                    });
                } catch (error) {
                    reject(error instanceof Error ? error : new Error(error));
                }
            } else {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
            }
        });
    }

    nativeNotify(message: string, title?: string, id?: number, actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify> {
        return new CancelablePromise<INativeNotify>((resolve, reject) => {
            let notification: INativeNotify = {
                    id: 0,
                    text: message,
                    title: title,
                    icon: "res://ic_stat_notification",
                    smallIcon: "res://ic_stat_notification",
                    priority: 1
            };
            this.createNotificationId(id).then(
                result => {
                    notification.id = result;
                    if (actions) {
                        notification.actions = actions;
                    }
                    this.requestNotificationPermission().then(
                        _ => {
                            this.sendNotificationAndResolve(resolve, reject, notification);
                        },
                        error => {
                            reject(error);
                        }
                    ).catch(reject);
                },
                error => {
                    reject(error);
                }
            ).catch(reject);
        });
    }

    sendNotificationAndResolve(resolve, reject, notification: INativeNotify) {
        if (window.geotabModules) {
            try {
                window.geotabModules.localNotification.schedule(notification, (err, result) => {
                    if (err) {
                        logger.error(logger.Source.NATIVE, `nativeNotify: Failed to send notification: ${ err?.message } (AppType: NativeSdk)`);
                        reject(err);
                    } else if (!result) {
                        logger.error(logger.Source.NATIVE, `nativeNotify: Failed to schedule notification: ${ getNotificationTextForLogging(notification) } (AppType: NativeSdk)`);
                        reject(new Error("Native SDK failed to schedule the notification"));
                    } else {
                        logger.log(logger.Source.NATIVE, `nativeNotify: Sent notification: ${ getNotificationTextForLogging(notification) } (AppType: NativeSdk)`);
                        resolve(notification);
                    }
                });
            } catch (error) {
                reject(error instanceof Error ? error : new Error(error));
            }
        } else {
            reject(new Error(NATIVESDK_UNDEFINED_MSG));
        }
    }

    updateNotification (message: string, title: string, id: number) {
        return this.nativeNotify(message, title, id);
    }

    getActiveNotifications(): CancelablePromise<INativeNotify[]> {
        return new CancelablePromise<INativeNotify[]>((resolve, reject) => {
            if (window.geotabModules) {
                try {
                    window.geotabModules.localNotification.getAll(undefined, (_err, result) => {
                        resolve(result || []);
                    });
                } catch (error) {
                    reject(error instanceof Error ? error : new Error(error));
                }
            } else {
                reject(new Error(NATIVESDK_UNDEFINED_MSG));
            }
        });
    }

    nativeNotifyEventOn(eventName: string, callback: TNativeActionEventCallback) {
        try {
            if (window.geotabModules) {
                window.geotabModules.localNotification.on(eventName, callback);
            }
        } catch (error) {
            handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
        }
    }

    nativeNotifyEventOff(eventName: string, callback: TNativeActionEventCallback) {
        try {
            if (window.geotabModules) {
                window.geotabModules.localNotification.off(eventName, callback);
            }
        } catch (error) {
            handleJSNative(error instanceof Error ? error : new Error(error), true, "nativeUnhandledException");
        }
    }

    cancelNativeNotification(id: number): CancelablePromise<string> {
        return new CancelablePromise((resolve, reject) => {
            try {
                if (!window.geotabModules) {
                    reject(NATIVESDK_UNDEFINED_MSG);
                    return;
                }
                window.geotabModules?.localNotification.cancel(id, (err, result) => {
                    if (err || !result) {
                        reject(new Error(`Notification with id ${ id } doesn't not exist`));
                    } else {
                        resolve(`Notification with id ${ id } is now cancelled`);
                    }
                });
            } catch (error) {
                reject(error instanceof Error ? error : new Error(error));
            }
        });
    }
}

class WebNotificationController implements INotificationController {
    hasNotificationPermission(): CancelablePromise<string> {
        return CancelablePromise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    requestNotificationPermission(): CancelablePromise<string> {
        return CancelablePromise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    nativeNotify(_message: string, _title?: string, _id?: number, _actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify> {
        return CancelablePromise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateNotification(_message: string, _title?: string, _id?: number, _actions?: INativeNotifyAction[]): CancelablePromise<INativeNotify> {
        return CancelablePromise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getActiveNotifications(): CancelablePromise<INativeNotify[]> {
        return CancelablePromise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    nativeNotifyEventOn() { }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    nativeNotifyEventOff(_eventName: string, _callback: TNativeActionEventCallback) { }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cancelNativeNotification(_id: number): CancelablePromise<string> {
        return CancelablePromise.reject(new Error(NOT_NATIVE_APP_MSG));
    }
}

let cordovaNotificationController: INotificationController = new CordovaNotificationController();
let nativeSDKNotificationController: INotificationController = new NativeSDKNotificationController();
let webAppNotificationController: INotificationController = new WebNotificationController();
/**
 * @returns notificationController based on current AppType
 */
export let getNotificationController = () => {
    switch (getAppType()) {
        case AppType.CordovaApp:
            return cordovaNotificationController;
        case AppType.NativeSdk:
            return nativeSDKNotificationController;
        default:
            return webAppNotificationController;
    }
};
